import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  Divider, 
  Button, 
  Chip, 
  useTheme,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  Warning, 
  DonutLarge, 
  ArrowForward, 
  Info, 
  ArrowUpward,
  ArrowDownward,
  CompareArrows,
  PieChart,
  BarChart,
  CheckCircle,
  Cancel,
  BarChart as MarketCapIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';

// Styled components
const AllocationBar = styled(LinearProgress)<{ value: number }>(({ theme, value }) => ({
  height: 10,
  borderRadius: 5,
  backgroundColor: theme.palette.grey[200],
  '.MuiLinearProgress-bar': {
    borderRadius: 5,
  }
}));

// Define props
interface MarketAllocationViewProps {
  marketReport: any;
  onTickerSelect?: (ticker: string) => void;
  renderMarketRating: (rating: string) => React.ReactElement;
  renderMarketTrend: (trend: string) => React.ReactElement;
  onTabChange: (index: number, subtab?: number) => void;
}

const MarketAllocationView: React.FC<MarketAllocationViewProps> = ({ 
  marketReport, 
  onTickerSelect,
  renderMarketRating,
  renderMarketTrend,
  onTabChange
}) => {
  const theme = useTheme();
  console.log('Market Report:', marketReport); // Debug log
  const report = marketReport?.market_report;
  
  if (!report) {
    return (
      <Alert severity="warning">
        No market report data available. Please refresh or try again later.
      </Alert>
    );
  }

  // Get sector allocation data
  const sectorReports = report.Sectors?.Reports || [];
  const marketcapReports = report.MarketCaps?.Reports || [];
  
  // Get asset class allocation data (mock data for now, replace with actual data)
  let [stocksIdeal, stocksMax, stocksMin] = [report.MarketAllUp.Report.Allocations.ideal, report.MarketAllUp.Report.Allocations.max, report.MarketAllUp.Report.Allocations.min];
  if (stocksIdeal > stocksMax) {
    [stocksIdeal, stocksMax] = [stocksMax, stocksIdeal];
  }
  if (stocksIdeal < stocksMin) {
    [stocksIdeal, stocksMin] = [stocksMin, stocksIdeal];
  }
  // console.log('Stocks Ideal:', stocksIdeal, 'Max:', stocksMax, 'Min:', stocksMin); // Debug log
  const assetClassAllocation = [
    { name: 'Stocks', current: 80, 
        recommended: stocksIdeal, min: stocksMin, max: stocksMax },
    { name: 'Cash', current: 20, 
        recommended: 100 - stocksIdeal, min: 100 - stocksMax, max: 100 - stocksMin },
  ];
  
  // Calculate allocation status
  const getAllocationStatus = (current: number, recommended: number) => {
    const diff = Math.abs(current - recommended);
    if (diff <= 3) return { color: theme.palette.success.main, status: 'Aligned' };
    if (diff <= 10) return { color: theme.palette.warning.main, status: 'Adjust' };
    return { color: theme.palette.error.main, status: 'Rebalance' };
  };
  
  // Helper function to get status color
  const getStatusColor = (status: string) => {
    if (!status) return theme.palette.grey[500];
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('bullish')) {
      return theme.palette.success.main;
    } else if (statusLower.includes('bearish')) {
      return theme.palette.error.main;
    }
    return theme.palette.warning.main;
  };

  return (
    <Box>
      {/* Asset class allocation */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <DonutLarge sx={{ mr: 1, color: theme.palette.primary.main }} />
          Asset Class Allocation
        </Typography>
        
        <TableContainer sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Asset Class</TableCell>
                <TableCell align="right">Current %</TableCell>
                <TableCell align="right">Recommended %</TableCell>
                <TableCell align="right">Min-Max Range</TableCell>
                <TableCell>Allocation</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assetClassAllocation.map((asset, index) => {
                const { color, status } = getAllocationStatus(asset.current, asset.recommended);
                
                return (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      {asset.name}
                    </TableCell>
                    <TableCell align="right">{asset.current}%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      {asset.recommended}%
                    </TableCell>
                    <TableCell align="right">{asset.min}% - {asset.max}%</TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <AllocationBar 
                        variant="determinate" 
                        value={asset.recommended}
                        sx={{
                          '.MuiLinearProgress-bar': {
                            backgroundColor: color
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={status}
                        size="small"
                        sx={{ 
                          bgcolor: color,
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* <Box sx={{ bgcolor: theme.palette.background.paper, p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Recommendation Summary:
          </Typography>
          <Typography variant="body2">
            Based on the current market conditions, we recommend slightly increasing your equity allocation and reducing your bond exposure. Maintain a small cash position for opportunistic investments.
          </Typography>
        </Box> */}
      </Paper>
      
      {/* Sector allocation */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <PieChart sx={{ mr: 1, color: theme.palette.primary.main }} />
          Sector Allocation (Equity Portion)
        </Typography>
        
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sector</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell align="right">Current %</TableCell>
                <TableCell align="right">Recommended %</TableCell>
                <TableCell align="right">Min-Max Range</TableCell>
                <TableCell>Allocation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sectorReports.map((sector: any, index: number) => {
                // Mock current allocation for demonstration
                const currentAllocation = Math.floor(Math.random() * 15) + 1;
                const recommendedAllocation = sector.Allocations?.ideal || 0;
                const { color } = getAllocationStatus(currentAllocation, recommendedAllocation);
                
                return (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      {sector.Classification.replace('Sector: ', '')}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={sector.Rating}
                        size="small"
                        sx={{ 
                          bgcolor: getStatusColor(sector.Rating),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{currentAllocation}%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      {recommendedAllocation}%
                    </TableCell>
                    <TableCell align="right">
                      {sector.Allocations?.min || 0}% - {sector.Allocations?.max || 0}%
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <AllocationBar 
                        variant="determinate" 
                        value={recommendedAllocation}
                        sx={{
                          '.MuiLinearProgress-bar': {
                            backgroundColor: color
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Overweight Sectors
            </Typography>
            <List dense>
              {sectorReports
                .filter((s: any) => s.Rating.includes('BULLISH'))
                .slice(0, 3)
                .map((s: any, idx: number) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={s.Classification.replace('Sector: ', '')}
                      secondary={`Increase to ${s.Allocations?.ideal || 0}% (from current allocation)`}
                    />
                  </ListItem>
                ))
              }
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Underweight Sectors
            </Typography>
            <List dense>
              {sectorReports
                .filter((s: any) => s.Rating.includes('BEARISH'))
                .slice(0, 3)
                .map((s: any, idx: number) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <Cancel color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={s.Classification.replace('Sector: ', '')}
                      secondary={`Decrease to ${s.Allocations?.ideal || 0}% (from current allocation)`}
                    />
                  </ListItem>
                ))
              }
            </List>
          </Grid>
        </Grid>
      </Paper>

       {/* MarketCap allocation */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <MarketCapIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          MarketCap Allocation (Equity Portion)
        </Typography>
        
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sector</TableCell>
                <TableCell>Rating</TableCell>
                <TableCell align="right">Current %</TableCell>
                <TableCell align="right">Recommended %</TableCell>
                <TableCell align="right">Min-Max Range</TableCell>
                <TableCell>Allocation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {marketcapReports.map((marketcap: any, index: number) => {
                // Mock current allocation for demonstration
                const currentAllocation = Math.floor(Math.random() * 15) + 1;
                const recommendedAllocation = marketcap.Allocations?.ideal || 0;
                const { color } = getAllocationStatus(currentAllocation, recommendedAllocation);
                
                return (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                      {marketcap.Classification.replace('MarketCap: ', '')}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={marketcap.Rating}
                        size="small"
                        sx={{ 
                          bgcolor: getStatusColor(marketcap.Rating),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{currentAllocation}%</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      {recommendedAllocation}%
                    </TableCell>
                    <TableCell align="right">
                      {marketcap.Allocations?.min || 0}% - {marketcap.Allocations?.max || 0}%
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <AllocationBar 
                        variant="determinate" 
                        value={recommendedAllocation}
                        sx={{
                          '.MuiLinearProgress-bar': {
                            backgroundColor: color
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Overweight MarketCaps
            </Typography>
            <List dense>
              {marketcapReports
                .filter((s: any) => s.Rating.includes('BULLISH'))
                .slice(0, 3)
                .map((s: any, idx: number) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={s.Classification.replace('MarketCap: ', '')}
                      secondary={`Increase to ${s.Allocations?.ideal || 0}% (from current allocation)`}
                    />
                  </ListItem>
                ))
              }
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Underweight MarketCaps
            </Typography>
            <List dense>
              {marketcapReports
                .filter((s: any) => s.Rating.includes('BEARISH'))
                .slice(0, 3)
                .map((s: any, idx: number) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <Cancel color="error" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={s.Classification.replace('MarketCap: ', '')}
                      secondary={`Decrease to ${s.Allocations?.ideal || 0}% (from current allocation)`}
                    />
                  </ListItem>
                ))
              }
            </List>
          </Grid>
        </Grid>
      </Paper>
      
    
    </Box>
  );
};

export default MarketAllocationView; 